import { useMemo } from 'react';
import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PropertyOwnership } from '@/client';
import {
	getMyOutgoingTransfersUsersMeOutgoingTransfersGetOptions,
	getMyOutgoingTransfersUsersMeOutgoingTransfersGetQueryKey,
} from '@/client/@tanstack/react-query.gen';
import { PendingTransferStatusResponse } from '@/client/types.gen';
import { NOTIFICATIONS, showNotification } from '@/config/notifications';
import { isActive } from '@/utils/pendingTransferUtils';

interface UsePendingTransferProps {
	houseId: number;
}

export const pendingTransferQueryKey = (houseId: number) => ['pendingTransfer', houseId] as const;

export const pendingTransferQueryOptions = (houseId: number) =>
	queryOptions({
		queryKey: pendingTransferQueryKey(houseId),
		queryFn: async (): Promise<PendingTransferStatusResponse | null> => {
			try {
				const response = await PropertyOwnership.getPendingPropertyTransferHousesHouseIdPendingTransferGet({
					path: { house_id: houseId },
				});
				return response.data ?? null;
			} catch (error: unknown) {
				if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
					return null;
				}
				throw error;
			}
		},
		retry: false,
		refetchOnWindowFocus: false,
	});

/**
 * The set of property ids with an in-flight outgoing transfer, used to badge them as "Transferring".
 * One call to /users/me/outgoing-transfers (backend-scoped to the current user, already filtered to
 * active) replaces the old per-owned-property fan-out. isActive is a defensive re-filter.
 */
export const useOutgoingTransferPropertyIds = (): Set<number> => {
	const { data } = useQuery(getMyOutgoingTransfersUsersMeOutgoingTransfersGetOptions());

	return useMemo(() => new Set((data ?? []).filter(isActive).map((transfer) => transfer.property_id)), [data]);
};

/**
 * Hook for managing pending property transfer status and cancellation
 * Provides functionality to check if there's a pending transfer and cancel it
 */
export const usePendingTransfer = ({ houseId }: UsePendingTransferProps) => {
	const queryClient = useQueryClient();

	// Query to check pending transfer status
	const pendingTransferQuery = useQuery(pendingTransferQueryOptions(houseId));

	// Mutation to cancel pending transfer
	const cancelTransferMutation = useMutation({
		mutationFn: async () => {
			return PropertyOwnership.cancelPendingPropertyTransferHousesHouseIdPendingTransferDelete({
				path: { house_id: houseId },
				throwOnError: true,
			});
		},
		onSuccess: () => {
			// Invalidate the pending transfer query to refresh the status
			queryClient.invalidateQueries({ queryKey: pendingTransferQueryKey(houseId) });
			// Drop the "Transferring" badge on the properties list without a manual refresh.
			queryClient.invalidateQueries({ queryKey: getMyOutgoingTransfersUsersMeOutgoingTransfersGetQueryKey() });

			showNotification(NOTIFICATIONS.TRANSFER_OWNERSHIP.CANCELLED);
		},
		onError: (error) => {
			console.error('Cancel transfer failed:', error);
			showNotification(NOTIFICATIONS.TRANSFER_OWNERSHIP.CANCEL_FAILED);
		},
	});

	const hasPendingTransfer = !!pendingTransferQuery.data;
	const pendingTransfer = pendingTransferQuery.data;

	return {
		// Query state
		pendingTransfer,
		hasPendingTransfer,
		isLoading: pendingTransferQuery.isLoading,
		error: pendingTransferQuery.error,

		// Cancel functionality
		cancelTransfer: cancelTransferMutation.mutate,
		isCancelling: cancelTransferMutation.isPending,
		cancelError: cancelTransferMutation.error,

		// Utility functions
		refetch: pendingTransferQuery.refetch,
	};
};
